import { useRef, useMemo, useEffect, useState } from 'react';

import { RentDetailsProps } from './RentDetails.props';

import { useMutation } from '@tanstack/react-query';
import { QUERY_KEYS, useItem, useUser } from 'api/queries';
import { rentItem } from 'api/services/rent';
import { CommonModalRef } from 'components/modals/CommonModal/CommonModal.props';
import { routesSettings } from 'constants/routes';
import { useNavigate, useParams } from 'react-router-dom';
import { useRentItemStore } from 'state/atoms';
import { $itemRentDetails } from 'state/stores/rent-details';
import $session from 'state/stores/session/store';
import { RentPayload } from 'types/rent';
import { queryClient } from 'utils';
import { calculatePrice } from 'utils/product';
import RentDetailsView from './RentDetails.view';

const RentDetailsContainer = (props: RentDetailsProps) => {
  const productId = useParams().id!;

  const [{ startDate, endDate, pickupTimeBlock, dropoffTimeBlock }] =
    useRentItemStore();
  const navigate = useNavigate();

  const successModalRef = useRef<CommonModalRef>(null);
  const locationModalRef = useRef<CommonModalRef>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const { data } = useItem(productId);
  const userId = $session.actions.getUserId();
  const { data: userData } = useUser(userId || '');

  // Check if user has location set
  useEffect(() => {
    if (userData && (!userData.location || !userData.location.state)) {
      setShowLocationModal(true);
      locationModalRef.current?.onOpen();
    }
  }, [userData]);

  // Calculate pricing with tax
  const pricing = useMemo(() => {
    console.log('🔍 Pricing calculation - userData:', userData);
    console.log('🔍 Location:', userData?.location);
    console.log('🔍 State:', userData?.location?.state);
    console.log('🔍 City:', userData?.location?.city);

    if (!data || !startDate || !endDate || !userData?.location?.state) {
      console.log('❌ Pricing undefined - missing data:', {
        hasData: !!data,
        hasStartDate: !!startDate,
        hasEndDate: !!endDate,
        hasState: !!userData?.location?.state,
      });
      return undefined;
    }

    const localCalc = calculatePrice(
      data.price,
      new Date(startDate),
      new Date(endDate),
      pickupTimeBlock || undefined,
      dropoffTimeBlock || undefined,
    );

    const rentalAmount = localCalc.actualRentalPrice;
    const platformFee = rentalAmount * 0.15;

    // Get tax rate from user's location
    // City tax takes priority if available
    const taxRate =
      userData.location.city?.taxRate || userData.location.state?.taxRate || 0;
    const locationName =
      userData.location.city?.title || userData.location.state?.title || '';

    console.log('💰 Tax calculation:', {
      cityTaxRate: userData.location.city?.taxRate,
      stateTaxRate: userData.location.state?.taxRate,
      finalTaxRate: taxRate,
      locationName,
    });

    const taxableAmount = rentalAmount + platformFee;
    const tax = Math.round(((taxableAmount * taxRate) / 100) * 100) / 100;
    const totalAmount = taxableAmount + tax;

    const pricingResult = {
      rentalAmount,
      platformFee,
      tax,
      taxRate,
      taxState: locationName,
      totalAmount,
    };

    console.log('✅ Pricing result:', pricingResult);

    return pricingResult;
  }, [data, startDate, endDate, pickupTimeBlock, dropoffTimeBlock, userData]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const product = data;

      const renterId = $session.actions.getUserId();

      const payload: RentPayload = {
        renterId: renterId!,
        itemId: product?._id!,
        ownerId: product?.ownerId!,
        startDate: startDate!,
        endDate: endDate!,
        pickupTimeBlock: pickupTimeBlock!,
        dropoffTimeBlock: dropoffTimeBlock!,
      };

      return rentItem(payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.FETCH_RENT_DETAILS_KEY],
      });

      $itemRentDetails.actions.setRentCreationDate(data.createdAt);
      navigate(routesSettings.MAIN_PAYMENT.getPath(data._id), {
        replace: false,
      });
    },
  });

  const onCloseSuccessModal = () => {
    navigate(routesSettings.MAIN_RENT_PRODUCT.getPath(productId), {
      replace: false,
    });
  };

  const onCloseLocationModal = () => {
    navigate(routesSettings.MAIN_PROFILE.path, {
      replace: false,
    });
  };

  return (
    <RentDetailsView
      onCloseSuccessModal={onCloseSuccessModal}
      successModalRef={successModalRef}
      locationModalRef={locationModalRef}
      onCloseLocationModal={onCloseLocationModal}
      product={data}
      pricing={pricing}
      isLoading={isPending}
      onClickPayment={() => mutate()}
    />
  );
};

export default RentDetailsContainer;
